import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Inbox, Mail, Paperclip, CheckCircle2, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';
const fmtSize = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round(b / 1e3)} Ko`;

const StatusBadge = ({ status }) => {
  if (status === 'order_created') return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" />Dossier créé</Badge>;
  if (status === 'error') return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1"><AlertTriangle className="h-3 w-3" />Erreur</Badge>;
  return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1"><AlertTriangle className="h-3 w-3" />À trier</Badge>;
};

const EmailCard = ({ em, cabinets, onCreated }) => {
  const [cabinetId, setCabinetId] = useState('');
  const [busy, setBusy] = useState(false);

  const createOrder = async () => {
    if (!cabinetId) { toast.error('Choisissez un cabinet'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/email-inbox/${em.id}/create-order`, { cabinet_id: cabinetId });
      toast.success(`Dossier ${r.data.order_number} créé`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid={`inbox-email-${em.id}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{em.subject || '(sans objet)'}</p>
            <p className="text-xs text-muted-foreground truncate">
              De : <b>{em.from_name || em.from_email}</b> &lt;{em.from_email}&gt; · {fmtDT(em.received_at)}
            </p>
            {em.cabinet_name && <p className="text-xs text-muted-foreground">Cabinet : {em.cabinet_name}</p>}
          </div>
          <StatusBadge status={em.status} />
        </div>

        {em.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {em.attachments.map((a, i) => (
              <a key={i} href={`${API}/api/admin/email-inbox/${em.id}/attachments/${i}/download`}
                 className="inline-flex items-center gap-1 text-xs border rounded-full px-2.5 py-1 hover:bg-muted"
                 data-testid={`inbox-attachment-${em.id}-${i}`}>
                <Paperclip className="h-3 w-3" />{a.filename} <span className="text-muted-foreground">({fmtSize(a.size)})</span>
              </a>
            ))}
          </div>
        )}

        {em.status === 'order_created' && em.order_id && (
          <Link to={`/admin/orders/${em.order_id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`inbox-order-link-${em.id}`}>
            <ExternalLink className="h-3 w-3" />Dossier {em.order_number}
          </Link>
        )}

        {em.status !== 'order_created' && (
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
            <Select value={cabinetId} onValueChange={setCabinetId}>
              <SelectTrigger className="w-64 h-8 text-xs" data-testid={`inbox-cabinet-select-${em.id}`}>
                <SelectValue placeholder="Rattacher à un cabinet..." />
              </SelectTrigger>
              <SelectContent>
                {cabinets.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8" onClick={createOrder} disabled={busy || !em.attachments?.length} data-testid={`inbox-create-order-${em.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Créer le dossier'}
            </Button>
            {!em.attachments?.length && <span className="text-xs text-muted-foreground">Aucune pièce jointe exploitable</span>}
            {em.error && <span className="text-xs text-red-600">{em.error}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminEmailInbox() {
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [inbox, cls] = await Promise.all([
        axios.get(`${API}/api/admin/email-inbox`),
        axios.get(`${API}/api/clients`),
      ]);
      setEmails(inbox.data.emails);
      setCounts(inbox.data.counts || {});
      setCabinets(cls.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-email-inbox">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Inbox className="h-6 w-6" />Boîte email</h1>
            <p className="text-sm text-muted-foreground">Empreintes et ordonnances reçues par email — dossiers créés automatiquement.</p>
          </div>
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="inbox-count-created">{counts.order_created || 0} dossiers créés</Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="inbox-count-unmatched">{(counts.unmatched || 0) + (counts.error || 0)} à trier</Badge>
          </div>
        </div>

        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Mail className="h-4 w-4" />Adresse de réception</CardTitle></CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1">
            <p>Les cabinets envoient leurs fichiers (STL, ordonnance, photos) à : <b className="text-foreground">scan@jpdcloud.com</b></p>
            <p>Si l'expéditeur correspond à l'email d'un cabinet enregistré, le dossier est créé automatiquement. Sinon, l'email apparaît ici « À trier » : choisissez le cabinet et créez le dossier en un clic.</p>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : emails.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="inbox-empty">Aucun email reçu pour le moment.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {emails.map((em) => <EmailCard key={em.id} em={em} cabinets={cabinets} onCreated={fetchAll} />)}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
